import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useContentOverrides } from '../../lib/useContentOverrides';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Destination card with a synced image + city-name carousel.
 *
 * variant:
 *   - "featured"   → cinematic, larger aspect, overlaid caption.  One per region.
 *   - "supporting" → restrained, smaller, caption sits below the frame.
 */
function DestinationCard({ dest, variant = 'supporting' }) {
  const cities = useMemo(() => {
    if (!dest.short_description) return [];
    return dest.short_description.split(',').map(s => s.trim()).filter(Boolean);
  }, [dest.short_description]);

  const images = useMemo(() => {
    if (dest.carousel_images && dest.carousel_images.length > 0) return dest.carousel_images;
    const src = dest.external_hero_url || (dest.hero_image_url ? `${API}${dest.hero_image_url}` : null);
    return src ? [src] : [];
  }, [dest]);

  const [idx, setIdx] = useState(0);
  const [transitionKey, setTransitionKey] = useState(0);
  const [textVisible, setTextVisible] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const count = Math.max(cities.length, images.length);

  const HOLD_MS = variant === 'featured' ? 18000 : 15000;
  const FADE_MS = 3500;

  const advance = useCallback((delta, { instant = false } = {}) => {
    if (count <= 1) return;
    if (instant) {
      setIdx(prev => (prev + delta + count) % count);
      return;
    }
    setTextVisible(false);
    setTimeout(() => {
      setIdx(prev => (prev + delta + count) % count);
      setTransitionKey(k => k + 1);
      setTextVisible(true);
    }, FADE_MS / 2);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || isHovered) return;
    const timer = setInterval(() => advance(1), HOLD_MS);
    return () => clearInterval(timer);
  }, [count, isHovered, advance, HOLD_MS]);

  useEffect(() => {
    images.forEach(src => { const img = new Image(); img.src = src; });
  }, [images]);

  const currentCity = cities[idx % cities.length] || '';
  const hasImages = images.length > 0;
  const prevIdx = count > 0 ? (idx - 1 + count) % count : 0;

  const handleNav = (delta) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    advance(delta, { instant: true });
  };

  const isFeatured = variant === 'featured';
  const aspectClass = isFeatured ? 'aspect-[16/9]' : 'aspect-[3/2]';

  return (
    <Link
      to={`/blog?destination=${encodeURIComponent(dest.destination_name)}`}
      className="group block"
      data-testid={`dest-card-${dest.slug}`}
    >
      {hasImages ? (
        <div
          className={`${aspectClass} w-full rounded mb-4 overflow-hidden relative bg-[#1A1A1A]`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <img
            key={`bg-${prevIdx}`}
            src={images[prevIdx]}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            decoding="async"
          />
          <img
            key={`fg-${transitionKey}`}
            src={images[idx % images.length]}
            alt={currentCity || dest.destination_name}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03]"
            style={{
              animation: `verault-dest-fadein ${FADE_MS}ms ease-in-out forwards`,
              transition: 'transform 1200ms ease-out',
            }}
            decoding="async"
          />

          {/* Featured variant: gentle gradient + overlaid editorial caption */}
          {isFeatured && (
            <>
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent pointer-events-none"
              />
              <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-10 text-white">
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/70 mb-2">Featured</p>
                <h3 className="font-serif text-2xl sm:text-3xl leading-tight mb-2">{dest.destination_name}</h3>
                <p
                  className="font-accent italic text-sm sm:text-base text-white/85 h-5"
                  style={{
                    opacity: textVisible ? 1 : 0,
                    transition: `opacity ${FADE_MS / 2}ms ease-in-out`,
                  }}
                >
                  {currentCity}
                </p>
              </div>
            </>
          )}

          {count > 1 && (
            <>
              <button
                type="button"
                onClick={handleNav(-1)}
                aria-label="Previous photo"
                data-testid={`dest-${dest.slug}-prev`}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/30 hover:bg-black/55 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
              >
                <ChevronLeft className="w-5 h-5" strokeWidth={1.75} />
              </button>
              <button
                type="button"
                onClick={handleNav(1)}
                aria-label="Next photo"
                data-testid={`dest-${dest.slug}-next`}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/30 hover:bg-black/55 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
              >
                <ChevronRight className="w-5 h-5" strokeWidth={1.75} />
              </button>
            </>
          )}
        </div>
      ) : (
        <div className={`${aspectClass} bg-[#F0EDE6] rounded mb-4 group-hover:bg-[#E8E4DC] transition-colors`} />
      )}

      {/* Supporting variant keeps caption below the frame, quiet and editorial */}
      {!isFeatured && (
        <>
          <p className="font-serif text-base text-[#1A1A1A] group-hover:text-[#333] transition-colors">
            {dest.destination_name}
          </p>
          <p className="text-xs text-[#999] mt-1 h-4">
            <span
              className="inline-block"
              style={{
                opacity: textVisible ? 1 : 0,
                transition: `opacity ${FADE_MS / 2}ms ease-in-out`,
              }}
            >
              {currentCity}
            </span>
          </p>
        </>
      )}
    </Link>
  );
}

/**
 * One editorial chapter per region.
 *
 * Layout:
 *   Kicker (Chapter index)  ·  Region name  ·  Italic storytelling tagline
 *   ──────────────────────────────────────────────────────────────────────
 *   Featured destination (large cinematic card, overlaid caption)
 *   Supporting destinations (2-column restrained grid, max 4 visible)
 *   "Explore more from {Region}" — toggle reveals the remainder
 */
function RegionChapter({ region, index, anchor }) {
  const [expanded, setExpanded] = useState(false);

  const [featured, ...rest] = region.destinations;
  const VISIBLE_SUPPORTING = 4;
  const hasOverflow = rest.length > VISIBLE_SUPPORTING;
  const visible = expanded ? rest : rest.slice(0, VISIBLE_SUPPORTING);
  const hidden = rest.length - VISIBLE_SUPPORTING;

  return (
    <section
      id={`region-${anchor}`}
      className="scroll-mt-24"
      data-testid={`region-${anchor}`}
    >
      {/* Chapter header — kicker, name, tagline */}
      <div className="mb-8 sm:mb-12">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-3">
          Chapter {String(index + 1).padStart(2, '0')}
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] mb-3 leading-tight">
          {region.name}
        </h2>
        {region.tagline && (
          <p className="font-accent italic text-base sm:text-lg text-[#6B6B6B] max-w-xl leading-relaxed">
            {region.tagline}
          </p>
        )}
      </div>

      {/* Featured destination — cinematic anchor for the chapter */}
      {featured && (
        <div className="mb-8 sm:mb-12">
          <DestinationCard dest={featured} variant="featured" />
        </div>
      )}

      {/* Supporting destinations — restrained 2-up grid */}
      {visible.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 sm:gap-y-10">
          {visible.map(dest => (
            <DestinationCard key={dest.id} dest={dest} variant="supporting" />
          ))}
        </div>
      )}

      {/* Progressive reveal — hidden richness */}
      {hasOverflow && (
        <div className="mt-8 sm:mt-10 text-center">
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            className="group inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors"
            data-testid={`region-${anchor}-expand`}
          >
            <span className="w-12 h-px bg-[#E8E4DC] group-hover:bg-[#C9A961] transition-colors" aria-hidden="true" />
            {expanded
              ? `Show less`
              : `Explore ${hidden} more from ${region.name}`}
            <span className="w-12 h-px bg-[#E8E4DC] group-hover:bg-[#C9A961] transition-colors" aria-hidden="true" />
          </button>
        </div>
      )}
    </section>
  );
}

export default function DestinationsPage() {
  const c = useContentOverrides('destinations');
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    axios.get(`${API}/api/cms/destinations/published`)
      .then(res => setDestinations(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const regionGroups = useMemo(() => {
    const groups = {};
    destinations.forEach(d => {
      const r = d.region || 'Other';
      // Hide Middle East region from the public site (still authorable + visible in CMS).
      // Matches any region name containing "middle east" (case-insensitive).
      if (/middle\s*east/i.test(r)) return;
      if (!groups[r]) groups[r] = { name: r, tagline: d.region_tagline || '', destinations: [] };
      groups[r].destinations.push(d);
    });
    return Object.values(groups);
  }, [destinations]);

  // Deep-link support (#europe etc.) — wait for data so the region anchor exists
  useEffect(() => {
    if (loading) return;
    const hash = location.hash?.replace('#', '');
    if (!hash) return;
    requestAnimationFrame(() => {
      const el = document.getElementById(`region-${hash}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [loading, location.hash]);

  const regionAnchor = (name) =>
    name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  return (
    <div data-testid="page-destinations">
      {c.previewMode && (
        <div className="bg-[#1A1A1A] text-white text-center py-2 px-4 text-[11px] tracking-[0.22em] uppercase font-mono" data-testid="destinations-preview-banner">
          Studio draft preview &mdash; not yet published
        </div>
      )}
      {/* Hero — atmospheric, editorial (tightened on mobile) */}
      <section className="pt-20 pb-12 sm:pt-32 sm:pb-20 px-6 text-center" data-testid="destinations-hero">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-6">
          {c.get('hero.kicker', 'A World, Curated')}
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] max-w-3xl mx-auto leading-tight">
          {c.get('hero.title', 'Where We Travel')}
        </h1>
        <div className="w-12 h-px bg-[#E8E4DC] mx-auto my-6 sm:my-8" aria-hidden="true" />
        <p className="font-accent italic text-base sm:text-lg text-[#6B6B6B] max-w-xl mx-auto leading-relaxed">
          {c.get('hero.subtitle', 'Destinations we know in detail \u2014 from the coastline of the Mediterranean to the quiet cities of the far east. Each one chosen for its character, not its checklist.')}
        </p>
      </section>

      {/* Region chapters — editorial pacing, intentional but not floating */}
      <section className="bg-white border-t border-[#E8E4DC]" data-testid="destinations-regions">
        <div className="max-w-5xl mx-auto px-6 py-16 sm:py-24">
          {loading ? (
            <p className="text-sm text-[#999] text-center py-12">Loading...</p>
          ) : regionGroups.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-accent italic text-base text-[#6B6B6B]">A new chapter is being prepared.</p>
            </div>
          ) : (
            <div className="space-y-16 sm:space-y-28 divide-y divide-[#F0EDE6]">
              {regionGroups.map((region, i) => (
                <div key={region.name} className={i === 0 ? '' : 'pt-16 sm:pt-28'}>
                  <RegionChapter
                    region={region}
                    index={i}
                    anchor={regionAnchor(region.name)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA — quiet, intentional */}
      <section className="py-16 sm:py-24 px-6 text-center bg-[#F5F3EE]" data-testid="destinations-cta">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-6">
          {c.get('cta.kicker', 'Beyond The Map')}
        </p>
        <p className="font-serif text-2xl sm:text-3xl text-[#1A1A1A] max-w-xl mx-auto leading-snug mb-3">
          {c.get('cta.title', 'Don\u2019t see your destination?')}
        </p>
        <p className="font-accent italic text-base sm:text-lg text-[#6B6B6B] max-w-md mx-auto mb-10">
          {c.get('cta.message', 'We travel everywhere our clients dream.')}
        </p>
        <Link
          to="/plan"
          className="inline-block px-8 py-3.5 bg-[#1A1A1A] text-white text-sm tracking-wide rounded hover:bg-[#333] transition-colors"
          data-testid="destinations-cta-button"
        >
          {c.get('cta.button_label', 'Begin Your Experience')}
        </Link>
      </section>
    </div>
  );
}
